import { useCallback, useEffect, useMemo, useRef } from "react";
import { useRouter } from "next/router";
import { useBreakpoints } from "@otrium/core";
import { Modal } from "src/atoms/Modal";
import AddedToCartContent from "./AddedToCartContent";
import {
  useAddedProdToCart,
  useCartMinLineItemsTotalPriceDiff,
  useAppActions,
} from "src/hooks/stores/use-app-store";
import { useCartData } from "src/hooks/stores/use-cart-store";
import { POPUP_AUTO_DISMISS_DELAY } from "src/utils/constants";

const AddedToCart = (): JSX.Element | null => {
  const addedProdToCart = useAddedProdToCart();
  const cartMinLineItemsTotalPriceDiff = useCartMinLineItemsTotalPriceDiff();
  const { setAddedProdToCart } = useAppActions();

  const { isMobile } = useBreakpoints();
  const router = useRouter();
  const { cart } = useCartData();
  const cartTotal = cart?.taxedPrice?.totalGross || cart?.totalPrice;

  const isOpen = Boolean(addedProdToCart);
  const isCartPage = useMemo(
    () => router.pathname.startsWith("/cart"),
    [router.pathname]
  );

  const handleClose = useCallback((): void => {
    setAddedProdToCart(null);
  }, [setAddedProdToCart]);

  const autoDismissTimer = useRef<number>();

  // Every interaction restarts the countdown, so the popup closes five seconds after the user stops engaging with it
  const restartAutoDismiss = useCallback((): void => {
    window.clearTimeout(autoDismissTimer.current);
    autoDismissTimer.current = window.setTimeout(
      handleClose,
      POPUP_AUTO_DISMISS_DELAY
    );
  }, [handleClose]);

  useEffect(() => {
    const handleRouteChange = () => {
      handleClose();
    };

    if (isOpen) {
      router.events.on("routeChangeStart", handleRouteChange);
    }

    return () => {
      router.events.off("routeChangeStart", handleRouteChange);
    };
  }, [handleClose, router.events, isOpen]);

  useEffect(() => {
    if (!addedProdToCart || isCartPage) {
      return;
    }

    restartAutoDismiss();

    return () => window.clearTimeout(autoDismissTimer.current);
  }, [addedProdToCart, isCartPage, restartAutoDismiss]);

  if (!addedProdToCart || isCartPage) {
    return null;
  }

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      fullWidth={isMobile}
      fullHeight={false}
      hasMobileBottomAlign={isMobile}
      centerOnScreen={!isMobile}
      rounded={!isMobile}
    >
      <AddedToCartContent
        {...addedProdToCart}
        showAddedProduct={isMobile}
        cartMinLineItemsTotalPriceDiff={cartMinLineItemsTotalPriceDiff}
        cartTotal={cartTotal}
        onClose={handleClose}
        onInteraction={restartAutoDismiss}
      />
    </Modal>
  );
};

export default AddedToCart;
